import { useEffect, useState } from "react";
import { Loader2, Info } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { api } from "@/lib/api";
import { KpiCard, Panel, money, OVERLINE, H1 } from "@/components/admin/ui";

const RANGES = [["today", "Today"], ["7d", "7 Days"], ["30d", "30 Days"], ["month", "This Month"], ["year", "This Year"], ["all", "All Time"]];

export default function AdminRevenue() {
  const [range, setRange] = useState("all");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.adminRevenue({ range }).then(setD).catch(() => {}); }, [range]);

  const breakdown = d ? [
    { name: "Subscriptions", value: d.subscription_revenue_cents, color: "#1F2A22" },
    { name: "Marketplace Fees", value: d.marketplace_fee_revenue_cents, color: "#FF6A00" },
    { name: "Advertising", value: d.advertising_revenue_cents, color: "#8A5A44" },
    { name: "State Partnerships", value: d.state_partnership_revenue_cents, color: "#5C6B61" },
  ] : [];

  return (
    <div className="p-6 md:p-8" data-testid="admin-revenue">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div><span className={OVERLINE}>Finance</span><h1 className={`${H1} mt-1`}>HomeRange Revenue</h1></div>
        <div className="flex flex-wrap gap-1 border border-[#E5E2DC] bg-white p-1" data-testid="revenue-range">
          {RANGES.map(([k, l]) => (
            <button key={k} onClick={() => setRange(k)} className={`px-3 py-1.5 text-xs font-semibold ${range === k ? "bg-[#1F2A22] text-[#F9F8F6]" : "text-[#5C6B61] hover:bg-[#F2F0EB]"}`} data-testid={`rev-range-${k}`}>{l}</button>
          ))}
        </div>
      </div>

      {!d ? <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <KpiCard label="HomeRange Revenue" value={money(d.homerange_revenue_cents)} accent testid="rev-homerange" />
            <KpiCard label="Net Revenue" value={money(d.net_revenue_cents)} testid="rev-net" sub={d.processing_fees_estimated ? "after est. processing fees" : "after processing fees"} />
            <KpiCard label="Gross Marketplace Sales" value={money(d.gmv_cents)} testid="rev-gmv" />
            <KpiCard label="Refunds" value={money(d.refunds_cents)} testid="rev-refunds" />
          </div>

          {/* Revenue equation */}
          <Panel title="How HomeRange net revenue is calculated" className="mt-6">
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-1 text-sm">
              {[["Gross Marketplace Sales (GMV)", d.gmv_cents, false],
                ["− Seller Earnings", -d.seller_earnings_cents, false],
                ["− Refunds", -d.refunds_cents, false],
                ["= Marketplace Fee Revenue", d.marketplace_fee_revenue_cents, true],
                ["+ Subscription Revenue", d.subscription_revenue_cents, false],
                ["+ Advertising Revenue", d.advertising_revenue_cents, false],
                ["+ State Partnership Revenue", d.state_partnership_revenue_cents, false],
                ["= HomeRange Revenue", d.homerange_revenue_cents, true],
                [`− Payment Processing Fees${d.processing_fees_estimated ? " (est.)" : ""}`, -d.processing_fees_cents, false],
                ["= Net Revenue", d.net_revenue_cents, true]].map(([label, val, strong]) => (
                <div key={label} className={`flex justify-between py-2 border-b border-[#E5E2DC] ${strong ? "font-semibold text-[#1F2A22]" : "text-[#5C6B61]"}`}>
                  <span>{label}</span><span className="tabular-nums">{money(val)}</span>
                </div>
              ))}
            </div>
            {d.processing_fees_estimated && (
              <p className="flex items-start gap-2 text-xs text-[#8A5A44] mt-4"><Info size={14} className="mt-0.5 shrink-0" /> Processing fees are estimated (2.9% + 30¢/charge) until live Stripe balance-transaction fees are captured via webhooks in production.</p>
            )}
          </Panel>

          {/* Breakdown chart */}
          <Panel title="Revenue by source" className="mt-6">
            {d.homerange_revenue_cents === 0 ? (
              <div className="h-56 grid place-items-center text-sm text-[#5C6B61]">No HomeRange revenue in this period yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={breakdown.map((b) => ({ ...b, value: b.value / 100 }))}>
                  <CartesianGrid stroke="#E5E2DC" strokeDasharray="4 4" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#5C6B61", fontSize: 11 }} axisLine={{ stroke: "#E5E2DC" }} tickLine={false} />
                  <YAxis tick={{ fill: "#5C6B61", fontSize: 11 }} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, "Revenue"]} contentStyle={{ border: "1px solid #E5E2DC", borderRadius: 0 }} />
                  <Bar dataKey="value">{breakdown.map((b, i) => <Cell key={i} fill={b.color} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
